import { useMemo } from 'react';
import { useNavigation } from '@react-navigation/native';
import type { NativeStackNavigationProp } from '@react-navigation/native-stack';
import type { ActivationTaskId } from '../redux/ownerActivationSlice';
import { ACTIVATION_TASK_CONFIG } from '../screens/HomeScreen/components/activationTaskConfig';

/**
 * Task destination orchestration for the Home activation tracker (US5, T074).
 *
 * Uses existing app routes; the first-sale action redirects to Add product and
 * explains the dependency when no product exists yet. Back Office / staff /
 * location destinations are injected because they carry Home-scoped concerns
 * (owner auto-login, location picker) that must not be duplicated here.
 */

export type OwnerActivationTaskAction = {
  onPress: () => void;
  disabled: boolean;
  busy: boolean;
  dependencyNote?: string;
};

export interface OwnerActivationTaskActionDeps {
  /** Whether at least one real product exists (first-sale dependency). */
  hasProduct: boolean;
  /** Current location is switching: disables location-dependent actions. */
  isLocationSwitching: boolean;
  /** A test location exists to switch into; otherwise show "Still preparing". */
  testLocationReady: boolean;
  navigateAddProduct: () => void;
  navigateFirstSale: () => void;
  openBackOffice: () => void;
  openAddStaff: () => void;
  openLocationPicker: () => void;
  /** Refresh locations while test location is still provisioning. */
  refreshLocations: () => void;
}

/**
 * Pure mapping from readiness + injected destinations to per-task actions.
 * Exported for direct unit testing without a navigator.
 */
export const buildOwnerActivationTaskActions = (
  deps: OwnerActivationTaskActionDeps,
): Record<ActivationTaskId, OwnerActivationTaskAction> => {
  const {
    hasProduct,
    isLocationSwitching,
    testLocationReady,
    navigateAddProduct,
    navigateFirstSale,
    openBackOffice,
    openAddStaff,
    openLocationPicker,
    refreshLocations,
  } = deps;

  return {
    add_product: {
      onPress: navigateAddProduct,
      disabled: isLocationSwitching,
      busy: false,
    },
    first_sale: hasProduct
      ? {
          onPress: navigateFirstSale,
          disabled: isLocationSwitching,
          busy: false,
        }
      : {
          // Redirect + explain: pressing routes to Add product first.
          onPress: navigateAddProduct,
          disabled: isLocationSwitching,
          busy: false,
          dependencyNote: ACTIVATION_TASK_CONFIG.first_sale.dependencyNote,
        },
    explore_backoffice: {
      onPress: openBackOffice,
      disabled: false,
      busy: false,
    },
    add_staff: {
      onPress: openAddStaff,
      disabled: false,
      busy: false,
    },
    // Still preparing: CTA stays tappable for refresh/retry. Never completes
    // from picker open — only LOCATION_SWITCHED after confirmed switch.
    visit_test_location: testLocationReady
      ? {
          onPress: openLocationPicker,
          disabled: isLocationSwitching,
          busy: false,
        }
      : {
          onPress: refreshLocations,
          disabled: isLocationSwitching,
          busy: true,
        },
  };
};

export interface UseOwnerActivationTaskActionsParams {
  hasProduct: boolean;
  isLocationSwitching: boolean;
  testLocationReady: boolean;
  openBackOffice: () => void;
  openAddStaff: () => void;
  openLocationPicker: () => void;
  refreshLocations: () => void;
}

export const useOwnerActivationTaskActions = (
  params: UseOwnerActivationTaskActionsParams,
): Record<ActivationTaskId, OwnerActivationTaskAction> => {
  const navigation = useNavigation<NativeStackNavigationProp<any>>();
  const {
    hasProduct,
    isLocationSwitching,
    testLocationReady,
    openBackOffice,
    openAddStaff,
    openLocationPicker,
    refreshLocations,
  } = params;

  return useMemo(
    () =>
      buildOwnerActivationTaskActions({
        hasProduct,
        isLocationSwitching,
        testLocationReady,
        navigateAddProduct: () =>
          navigation.navigate('NewProduct', {
            source: 'activation_tracker',
            presentation: 'simplified',
          }),
        navigateFirstSale: () =>
          navigation.navigate('MainScreen', {
            screen: 'ProductsScreen',
            params: { from: 'HomeScreen' },
          }),
        openBackOffice,
        openAddStaff,
        openLocationPicker,
        refreshLocations,
      }),
    [
      hasProduct,
      isLocationSwitching,
      navigation,
      openAddStaff,
      openBackOffice,
      openLocationPicker,
      refreshLocations,
      testLocationReady,
    ],
  );
};

export default useOwnerActivationTaskActions;
